import { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { useEventListener } from 'utils/hooks'
import Box from '@mui/material/Box'
import Header from 'components/Header'
import Loader from 'components/Loader'
import Spiffy from 'components/Spiffy'
import AppRoutes from './Routes'
import TagManager from 'react-gtm-module'
import {
  GET_GENERAL_SETTING,
} from 'api/constants'
import {
  setUpdateReady,
} from 'slices/appSlice'
import {
  setAuthUser,
} from 'slices/globalSlice'
import { IS_PRODUCTION, IS_BOOKING, IS_DEV } from 'utils/constants'
import GlobalStyle from '../../global-styles'
import { useAuth0 } from '@auth0/auth0-react'
import { isEmpty } from 'lodash'
import { useAutomation } from 'utils/testHooks'

const AppWrapper = styled(Box).withConfig({
  shouldForwardProp: prop => ![
    'tabUsed',
  ].includes(prop),
})`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.palette.background.main.main};
  color: ${props => props.theme.palette.background.main.text};

  ${props => !props.tabUsed ? `
    * { outline: none; }
  ` : ''}
`

const App = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const isSWApplied = useRef(false)
  const { 
    isLoading, 
    isAuthenticated, 
    user: authUser,
  } = useAuth0()
  const [tabUsed, setTabUsed] = useState(false)
  const user = useSelector(({ global }) => global.user)
  const company = useSelector(({ global }) => global.company)
  const drawerOpen = useSelector(({ app }) => app.drawerOpen)
  const spiffyReady = 
    isAuthenticated && 
    !!user?.unique_id && 
    !isEmpty(company)

  // useAutomation()

  const setDataLayer = useCallback(() => {
    if (IS_PRODUCTION) {
      TagManager.dataLayer({
        dataLayer: {
          userID: user.id,
          page: location.pathname,
        },
      })
    }
  }, [user?.id, location.pathname])

  const setSW = useCallback(async () => {
    if (!IS_DEV && !IS_BOOKING) {
      try {
        const OfflinePluginRuntime = await import('@lcdp/offline-plugin/runtime')

        OfflinePluginRuntime.install({
          onUpdateReady: () => {
            console.warn('update ready')
            dispatch(setUpdateReady({
              status: 'update-ready',
              cb: OfflinePluginRuntime.applyUpdate,
            }))
          },
          onUpdated: () => {
            dispatch(setUpdateReady({
              status: 'updated',
            }))
            window.location.reload(true)
          },
        })
      } catch (error) {
        console.error("Error importing '@lcdp/offline-plugin/runtime':", error)
      }
    }
}, [dispatch])


  useEventListener('keyup', e => {
    if (e.keyCode === 9) setTabUsed(true)
  })

  useEffect(() => {
    if (authUser?.email) {
      dispatch(setAuthUser(authUser))
      if (!isSWApplied.current) {
        setSW()
        isSWApplied.current = true
      }
    }
  }, [authUser?.email])

  useEffect(() => {
    if (user?.unique_id) {
      dispatch({ 
        type: GET_GENERAL_SETTING, 
        setting: 'ai_chatbot_disabled',
      })
    }
  }, [user?.unique_id])

  useEffect(() => {
    if (user?.id) setDataLayer()
    window.scrollTo(0, 0)
  }, [location.pathname, user?.id])
  
  if (isLoading) return <Loader position="fixed" variant="primary" />

  return (
    <AppWrapper 
      tabUsed={tabUsed}
      mb={{
        xx: 8,
        md: 0,
      }}
    >
      <GlobalStyle />
      <Header />
      <AppRoutes />
      {spiffyReady && !drawerOpen && <Spiffy />}
    </AppWrapper>
  )
}

export default App
